import React, { useEffect, useState } from "react";
import AudienceCards from "./AudienceCards";
import { Link } from "react-router-dom";
import useStudioAccount from "./useStudioAccount";
import StudioGate from "./StudioGate";
import { api } from "./shared";
import { MESSAGES, statusInfo, trackApi, trackErrorMessage } from "./trackApi";
import { relativeTime } from "../../utils/relativeTime";

export const greeting = (date = new Date()) => {
  const h = date.getHours();
  return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
};

const formatDate = (value) => {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const compact = (n) => {
  if (!Number.isFinite(n)) return "—";
  if (n >= 1000000) return `${(n / 1000000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return String(n);
};
const money = (n) => (Number.isFinite(n) ? `$${Number(n) % 1 === 0 ? Number(n) : Number(n).toFixed(2)}` : "—");

export const PERIOD_OPTIONS = [
  { key: "7d", label: "Last 7 days" },
  { key: "28d", label: "Last 28 days" },
  { key: "12m", label: "Last 12 months" },
  { key: "all", label: "All time" },
];

// "▲ +12% vs previous 28 days". The API compares the chosen period with the period of the same length before it; when there is
// nothing earlier to compare with (null) the card shows 0%. All time has nothing to compare with, so it shows only the extra text.
export const changeNote = (percent, compareLabel, extra = "") => {
  if (!compareLabel) return extra;
  const n = percent === null || percent === undefined || !Number.isFinite(Number(percent)) ? 0 : Number(percent);
  const arrow = n > 0 ? "▲ " : n < 0 ? "▼ " : "";
  return `${arrow}${n > 0 ? "+" : ""}${n}% vs ${compareLabel}${extra ? ` · ${extra}` : ""}`;
};

function Stat({ label, value, note, tone }) {
  const color = tone === "up" ? "text-green-700" : tone === "down" ? "text-red-700" : "text-gray-500";
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-5 py-4">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1" data-testid={`stat-${label}`}>{value}</p>
      {note && <p className={`text-xs mt-1 font-medium ${color}`}>{note}</p>}
    </div>
  );
}

const toneOf = (percent) => (Number(percent) > 0 ? "up" : Number(percent) < 0 ? "down" : undefined);
const moneyText = (amount) => (amount === null || amount === undefined ? "—" : money(Number(amount)));

function DashboardBody() {
  const [state, setState] = useState({ loading: true, tracks: [], licensing: [], followers: null, summary: null, error: "" });
  const [period, setPeriod] = useState("28d");
  const [refreshing, setRefreshing] = useState(false);
  const firstLoad = React.useRef(true);

  useEffect(() => {
    let cancelled = false;
    Promise.allSettled([trackApi.list(), trackApi.licensingList(), api.get("profile"), trackApi.dashboardSummary("28d")]).then(([t, l, p, d]) => {
      if (cancelled) return;
      setState({
        loading: false,
        tracks: t.status === "fulfilled" ? t.value : [],
        licensing: l.status === "fulfilled" ? l.value?.tracks || [] : [],
        followers: p.status === "fulfilled" ? p.value?.data?.result?.stats?.followers ?? null : null,
        summary: d.status === "fulfilled" ? d.value || null : null,
        error: t.status === "rejected" ? trackErrorMessage(t.reason, MESSAGES[500]) : "",
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Changing the period only reloads the summary (the first load above already fetched the default period).
  useEffect(() => {
    if (firstLoad.current) {
      firstLoad.current = false;
      return undefined;
    }
    let cancelled = false;
    setRefreshing(true);
    trackApi
      .dashboardSummary(period)
      .then((summary) => !cancelled && setState((prev) => ({ ...prev, summary: summary || prev.summary })))
      .catch(() => {})
      .finally(() => !cancelled && setRefreshing(false));
    return () => {
      cancelled = true;
    };
  }, [period]);

  if (state.loading) return <DashboardSkeleton />;

  const { tracks, licensing, followers, summary, error } = state;
  const earningsByTrack = new Map(licensing.map((r) => [r.track_id, Number(r.earnings?.total) || 0]));
  const streams = tracks.map((t) => t.stream_count).filter((n) => Number.isFinite(n));
  const totalStreams = streams.length ? streams.reduce((a, b) => a + b, 0) : null;
  const licensingTotal = licensing.length ? [...earningsByTrack.values()].reduce((a, b) => a + b, 0) : null;
  const top = [...tracks].sort((a, b) => (b.stream_count ?? -1) - (a.stream_count ?? -1)).slice(0, 4);
  const recent = [...tracks].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)).slice(0, 5);
  const byId = new Map(tracks.map((t) => [t.id, t]));
  const rows = summary
    ? summary.top_tracks.map((r) => ({
        id: r.track_id,
        title: r.title,
        artwork: byId.get(r.track_id)?.artwork,
        streams: r.streams,
        sold: r.licences_sold,
        royalty: moneyText(r.streaming_royalty),
        earnings: moneyText(r.licensing_earnings),
      }))
    : top.map((t) => ({
        id: t.id,
        title: t.title,
        artwork: t.artwork,
        streams: t.stream_count,
        sold: null,
        royalty: "—",
        earnings: earningsByTrack.has(t.id) ? money(earningsByTrack.get(t.id)) : "—",
      }));
  const activity = summary?.recent_activity || [];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3 -mt-2">
        <p className="text-sm text-gray-600">Here's how your catalogue is performing.</p>
        {summary && (
          <div className="flex flex-wrap items-center gap-3">
            <div role="group" aria-label="Period" className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
              {PERIOD_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  onClick={() => setPeriod(o.key)}
                  aria-pressed={period === o.key}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md ${period === o.key ? "bg-[#095228] text-white" : "text-gray-700 hover:bg-gray-100"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {summary.updated_at && <span className="text-xs text-gray-500">Updated {relativeTime(summary.updated_at)}</span>}
          </div>
        )}
      </div>
      {error && <p className="rounded-xl bg-red-50 border border-red-200 text-red-800 px-4 py-3 text-sm" role="alert">{error}</p>}
      {summary?.is_empty && (
        <p className="rounded-xl bg-white border border-gray-200 px-4 py-3 text-sm text-gray-700">
          No data yet. Upload your first track to see how it performs.
        </p>
      )}
      <div className={`grid sm:grid-cols-2 xl:grid-cols-4 gap-4 ${refreshing ? "opacity-60" : ""}`} aria-busy={refreshing}>
        {summary ? (
          <>
            <Stat label="Total Streams" value={compact(summary.total_streams.total)} note={changeNote(summary.total_streams.change_percent, summary.period.compare_label, `${compact(summary.total_streams.in_period)} plays`)} tone={toneOf(summary.total_streams.change_percent)} />
            <Stat label="Followers" value={compact(summary.followers.total)} note={changeNote(summary.followers.change_percent, summary.period.compare_label, `${summary.followers.new_in_period} new`)} tone={toneOf(summary.followers.change_percent)} />
            <Stat
              label="Streaming Royalties"
              value={moneyText(summary.streaming_royalty.amount)}
              note={[changeNote(summary.streaming_royalty.change_percent, summary.period.compare_label), summary.streaming_royalty.estimated ? "Estimated" : ""].filter(Boolean).join(" · ")}
              tone={toneOf(summary.streaming_royalty.change_percent)}
            />
            <Stat
              label="Licensing Sales"
              value={moneyText(summary.licensing_sales.amount)}
              note={[changeNote(summary.licensing_sales.change_percent, summary.period.compare_label), `from ${summary.licensing_sales.licences_sold} ${summary.licensing_sales.licences_sold === 1 ? "licence" : "licences"} sold`].filter(Boolean).join(" · ")}
              tone={toneOf(summary.licensing_sales.change_percent)}
            />
          </>
        ) : (
          <>
            <Stat label="Total Streams" value={compact(totalStreams)} />
            <Stat label="Followers" value={followers === null ? "—" : compact(Number(followers))} />
            <Stat label="Streaming Royalties" value="—" note="Not available yet" />
            <Stat label="Licensing Sales" value={money(licensingTotal)} />
          </>
        )}
      </div>

      <section className="rounded-xl border border-gray-200 bg-[#f3f7f4] p-5">
        <h2 className="text-base font-semibold text-gray-900 mb-3">Top tracks</h2>
        {rows.length === 0 ? (
          <p className="text-sm text-gray-600">
            You haven't uploaded anything yet.{" "}
            <Link to="/artist-studio/tracks/new" className="font-semibold underline">Upload your first track</Link>
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="text-xs font-bold uppercase tracking-wide" style={{ color: "#095228" }}>
                <tr>
                  <th className="py-2 pr-4">Track</th>
                  <th className="py-2 pr-4">Streams</th>
                  <th className="py-2 pr-4">Licenses sold</th>
                  <th className="py-2 pr-4">Streaming royalty</th>
                  <th className="py-2">Licensing earnings</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr key={t.id} className="border-t border-gray-200">
                    <td className="py-3 pr-4">
                      <Link to={`/artist-studio/tracks/${t.id}`} className="flex items-center gap-3 font-medium text-gray-900 hover:underline">
                        <span className="w-9 h-9 rounded-lg bg-gray-200 overflow-hidden shrink-0">
                          {t.artwork?.url && <img src={t.artwork.url} alt="" className="w-full h-full object-cover" />}
                        </span>
                        {t.title || "Untitled draft"}
                      </Link>
                    </td>
                    <td className="py-3 pr-4 text-gray-800">{t.streams === null || t.streams === undefined ? "—" : compact(t.streams)}</td>
                    <td className="py-3 pr-4 text-gray-800">{t.sold === null ? "—" : t.sold}</td>
                    <td className="py-3 pr-4 text-gray-800">{t.royalty}</td>
                    <td className="py-3 text-gray-800">{t.earnings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {summary && !summary.is_empty && <AudienceCards period={period} />}

      <section className="rounded-xl border border-gray-200 bg-[#f3f7f4] p-5">
        <h2 className="text-base font-semibold text-gray-900 mb-3">Recent activity</h2>
        {activity.length > 0 ? (
          <ul className="divide-y divide-gray-200">
            {activity.map((a) => (
              <li key={a.id} className="py-2.5 text-sm text-gray-800 flex flex-wrap items-baseline justify-between gap-x-3">
                <span>
                  {a.link ? <Link to={a.link} className="hover:underline">{a.description}</Link> : a.description}
                  {a.amount !== null && a.amount !== undefined && <span className="font-medium"> — {money(Number(a.amount))}</span>}
                </span>
                <span className="text-gray-500 text-xs" title={formatDate(a.date)}>{relativeTime(a.date)}</span>
              </li>
            ))}
          </ul>
        ) : recent.length === 0 ? (
          <p className="text-sm text-gray-600">Nothing yet.</p>
        ) : (
          <ul className="divide-y divide-gray-200">
            {recent.map((t) => (
              <li key={t.id} className="py-2.5 text-sm text-gray-800">
                "{t.title || "Untitled draft"}" — {statusInfo(t.display_status).label}
                <span className="text-gray-500"> · {formatDate(t.updated_at)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}


// Grey placeholder shapes in the same layout as the real dashboard (period buttons, four cards, top tracks), shown while the first load runs.
function DashboardSkeleton() {
  const bar = "rounded bg-gray-100 animate-pulse";
  return (
    <div role="status" aria-label="Loading the dashboard" data-testid="dashboard-skeleton">
      <div className="flex flex-wrap gap-2">
        {[0, 1, 2, 3].map((i) => <div key={i} className={`h-9 w-28 rounded-full ${bar}`} />)}
      </div>
      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-2xl border border-gray-200 bg-white p-5">
            <div className={`h-3.5 w-24 ${bar}`} />
            <div className={`mt-4 h-8 w-32 ${bar}`} />
            <div className={`mt-3 h-3 w-28 ${bar}`} />
          </div>
        ))}
      </div>
      <div className="mt-6 rounded-2xl border border-gray-200 bg-white p-5">
        <div className={`h-4 w-32 ${bar}`} />
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="mt-4 flex items-center gap-3">
            <div className={`h-10 w-10 shrink-0 ${bar}`} />
            <div className={`h-3.5 flex-1 ${bar}`} />
            <div className={`h-3.5 w-16 ${bar}`} />
          </div>
        ))}
      </div>
      <span className="sr-only">Loading…</span>
    </div>
  );
}

export default function StudioDashboard() {
  const account = useStudioAccount();
  const first = (account.application?.artist_name || "").trim().split(/\s+/)[0];
  return (
    <StudioGate
      account={account}
      active="dashboard"
      heading={first ? `${greeting()}, ${first}` : greeting()}
      tabs={[]}
      maxWidth="max-w-5xl"
    >
      <DashboardBody />
    </StudioGate>
  );
}
